import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { createUserProfile, getUserProfile, saveTheme } from "../lib/db";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const p = await getUserProfile();
        if (p) {
          setProfile(p);
          if (p.theme) {
            setTheme(p.theme);
            document.body.classList.toggle("light", p.theme === "light");
          }
        } else {
          await createUserProfile({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName?.split(" ")[0] ||
                  firebaseUser.email?.split("@")[0] || "Student",
            school: "",
            programme: "",
          });
          const newProfile = await getUserProfile();
          setProfile(newProfile);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signUp = async (email, password, userData) => {
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(newUser);
    await createUserProfile({
      uid: newUser.uid,
      email: newUser.email,
      name: userData.name,
      school: "",
      programme: "",
    });
  };

  const signIn = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const p = await getUserProfile();
    if (!p) {
      await createUserProfile({
        uid: result.user.uid,
        email: result.user.email,
        name: result.user.displayName?.split(" ")[0] || "Student",
        school: "",
        programme: "",
      });
    }
    if (!result.user.emailVerified) {
      await sendEmailVerification(result.user);
    }
    return result;
  };

  const logout = () => signOut(auth);
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const resendVerification = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser({ ...auth.currentUser });
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.body.classList.toggle("light", newTheme === "light");
    if (auth.currentUser) await saveTheme(newTheme);
  };

  const value = {
    user,
    profile,
    loading,
    theme,
    toggleTheme,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
    resendVerification,
    refreshUser,
    setProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);